import React, { useState } from 'react';
import styles from './TabPanel.module.css';

export interface Tab {
  id: string;
  label: string;
}

interface TabBarProps {
  tabs: Tab[];
  selectedTab: string;
  onSelectedTabChange: (tabId: string) => void;
  editable?: boolean;
  onNewTabHandler?: () => Tab;
  onTabsChange?: (tabs: Tab[]) => void;
}

export const TabBar: React.FC<TabBarProps> = ({ tabs, selectedTab, onSelectedTabChange, onNewTabHandler, editable = false, onTabsChange }) => {
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);

  const handleAddTab = () => {
    if (!onTabsChange) return;

    const newTab: Tab = onNewTabHandler
      ? onNewTabHandler()
      : {
          id: `tab-${Date.now()}`,
          label: 'New Tab',
        };

    const updatedTabs = [...tabs, newTab];
    onTabsChange(updatedTabs);
    onSelectedTabChange(newTab.id);
  };

  const handleMoveTab = (tabId: string, direction: 'left' | 'right') => {
    if (!onTabsChange) return;

    const currentIndex = tabs.findIndex(tab => tab.id === tabId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= tabs.length) return;

    const updatedTabs = [...tabs];
    const [movedTab] = updatedTabs.splice(currentIndex, 1);
    updatedTabs.splice(newIndex, 0, movedTab);

    onTabsChange(updatedTabs);
  };

  const handleDeleteTab = (tabId: string, e: React.MouseEvent) => {
    if (!onTabsChange) return;
    e.stopPropagation();

    const updatedTabs = tabs.filter(tab => tab.id !== tabId);
    onTabsChange(updatedTabs);

    // If we deleted the selected tab, switch to the first available tab
    if (selectedTab === tabId && updatedTabs.length > 0) {
      onSelectedTabChange(updatedTabs[0].id);
    }
  };

  return (
    <div className={styles.tabList}>
      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          className={`${styles.tabWrapper} ${hoveredTabId === tab.id ? styles.hovered : ''}`}
          onMouseEnter={() => setHoveredTabId(tab.id)}
          onMouseLeave={() => setHoveredTabId(null)}
        >
          <button className={`${styles.tab} ${selectedTab === tab.id ? styles.active : ''}`} onClick={() => onSelectedTabChange(tab.id)}>
            <span>{tab.label}</span>
          </button>

          {editable && hoveredTabId === tab.id && (
            <div className={styles.tabControls}>
              {index > 0 && (
                <button
                  className={styles.controlButton}
                  onClick={e => {
                    e.stopPropagation();
                    handleMoveTab(tab.id, 'left');
                  }}
                  aria-label="Move left"
                >
                  ←
                </button>
              )}
              {index < tabs.length - 1 && (
                <button
                  className={styles.controlButton}
                  onClick={e => {
                    e.stopPropagation();
                    handleMoveTab(tab.id, 'right');
                  }}
                  aria-label="Move right"
                >
                  →
                </button>
              )}
              {tabs.length > 1 && (
                <button
                  className={`${styles.controlButton} ${styles.deleteButton}`}
                  onClick={e => handleDeleteTab(tab.id, e)}
                  aria-label="Delete tab"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {editable && (
        <button className={styles.addTabButton} onClick={handleAddTab} aria-label="Add tab">
          +
        </button>
      )}
    </div>
  );
};
